import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Globe2, Plus, Save, Star, Trash2, Warehouse as WarehouseIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTenant } from "./AppShell";
import { Badge, Button, Card, cx, Input } from "./ui";
import { api, ApiError, type WarehouseSettings } from "@/lib/api";

import { t, msg } from "@/lib/i18n";
const errMsg = (e: unknown, f: string) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? f) : f);

/**
 * Mehrere Lager je Kunde: welche Lager aktiv sind, Hauptlager, Reihenfolge der Ausweichlager und Länderregeln.
 * Ein Auftrag geht immer komplett an ein Lager – keine Teillieferungen.
 */
export function WarehousesCard({ editable }: { editable: boolean }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["shipping-methods", tenantId], queryFn: () => api.backend.shippingMethods(tenantId), staleTime: 5 * 60_000 });
  const initial = useMemo<WarehouseSettings | null>(() => {
    if (!q.data) return null;
    const list = [...q.data.warehouses].sort((a, b) => Number(!!b.primary) - Number(!!a.primary) || (a.priority ?? 99) - (b.priority ?? 99) || a.name.localeCompare(b.name));
    return {
      primaryId: q.data.activeWarehouseId ?? list[0]?.warehouseId ?? "",
      warehouses: list.map((w, i) => ({ warehouseId: w.warehouseId, active: w.active || !!w.primary, priority: i })),
      routing: q.data.routing ?? { countryRules: [], fallbackToOther: true },
    };
  }, [q.data]);
  const [s, setS] = useState<WarehouseSettings | null>(initial);
  useEffect(() => setS(initial), [initial]);
  const save = useMutation({
    mutationFn: (v: WarehouseSettings) => api.backend.setWarehouses(tenantId, v),
    onSuccess: () => {
      toast.success("Lager gespeichert", { description: msg("Gilt für alle neuen Aufträge.") });
      void qc.invalidateQueries({ queryKey: ["shipping-methods", tenantId] });
      void qc.invalidateQueries({ queryKey: ["backend", tenantId] });
    },
    onError: (e) => toast.error(errMsg(e, "Speichern fehlgeschlagen")),
  });
  if (!q.data || !s || q.data.warehouses.length < 2) return null;

  const meta = (id: string) => q.data!.warehouses.find((w) => w.warehouseId === id)!;
  const dirty = JSON.stringify(s) !== JSON.stringify(initial);
  const active = s.warehouses.filter((w) => w.active);
  const setW = (id: string, patch: Partial<WarehouseSettings["warehouses"][number]>) => setS({ ...s, warehouses: s.warehouses.map((w) => (w.warehouseId === id ? { ...w, ...patch } : w)) });
  const move = (i: number, d: -1 | 1) => {
    const list = [...s.warehouses];
    const [x] = list.splice(i, 1);
    list.splice(i + d, 0, x!);
    setS({ ...s, warehouses: list.map((w, k) => ({ ...w, priority: k })) });
  };
  const rules = s.routing.countryRules;
  const setRules = (r: typeof rules) => setS({ ...s, routing: { ...s.routing, countryRules: r } });

  return (
    <Card className="overflow-hidden">
      <div className="px-5 h-11 flex items-center gap-2 border-b border-border">
        <WarehouseIcon className="size-4 text-fg-subtle" />
        <span className="font-semibold text-[13px]">{t("Lager")}</span>
        <span className="text-[12px] text-fg-subtle">{active.length} {t("von")} {s.warehouses.length} {t("aktiv")}</span>
        {editable && dirty && <Button size="sm" variant="primary" className="ml-auto" icon={<Save className="size-3.5" />} loading={save.isPending} onClick={() => save.mutate(s)}>{t("Speichern")}</Button>}
      </div>

      <ul className="divide-y divide-border">
        {s.warehouses.map((w, i) => {
          const m = meta(w.warehouseId);
          const primary = s.primaryId === w.warehouseId;
          return (
            <li key={w.warehouseId} className={cx("px-5 py-3 flex flex-wrap items-center gap-3", !w.active && "opacity-60")}>
              <div className="min-w-[180px] flex-1">
                <div className="flex items-center gap-2 font-medium text-[13.5px]">
                  {m.name}
                  {primary && <Badge tone="success" icon={<Star className="size-3" />}>{t("Hauptlager")}</Badge>}
                </div>
                <div className="text-[12px] text-fg-muted">{[m.city, m.country].filter(Boolean).join(", ")} · {m.shippingMethods.length} {m.shippingMethods.length === 1 ? t("Versandart") : t("Versandarten")}</div>
              </div>
              {editable && (
                <div className="flex items-center gap-1">
                  <button type="button" role="switch" aria-checked={w.active} disabled={primary} onClick={() => setW(w.warehouseId, { active: !w.active })} className="inline-flex items-center gap-1.5 text-[12.5px] text-fg-muted mr-2 disabled:cursor-not-allowed" title={primary ? t("Das Hauptlager ist immer aktiv") : undefined}>
                    <span className={cx("w-7 h-4 rounded-full p-0.5 transition-colors", w.active ? "bg-ink" : "bg-border-strong")}><span className={cx("block size-3 rounded-full bg-surface transition-transform", w.active && "translate-x-3")} /></span>
                    {t("aktiv")}
                  </button>
                  {!primary && w.active && <Button size="sm" variant="ghost" onClick={() => setS({ ...s, primaryId: w.warehouseId })}>{t("Als Hauptlager")}</Button>}
                  <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="size-7 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 disabled:opacity-30" aria-label={t("früher fragen")}><ArrowUp className="size-3.5" /></button>
                  <button type="button" disabled={i === s.warehouses.length - 1} onClick={() => move(i, 1)} className="size-7 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 disabled:opacity-30" aria-label={t("später fragen")}><ArrowDown className="size-3.5" /></button>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="border-t border-border px-5 py-4 space-y-3 bg-surface-2/30">
        <label className="flex items-start gap-2 text-[13px] cursor-pointer">
          <input type="checkbox" disabled={!editable} checked={s.routing.fallbackToOther} onChange={(e) => setS({ ...s, routing: { ...s.routing, fallbackToOther: e.target.checked } })} className="mt-0.5 accent-ink" />
          <span>
            <span className="font-medium">{t("Ausweichlager nutzen")}</span>
            <span className="block text-[12px] text-fg-muted">{t("Hat das bevorzugte Lager nicht alles vorrätig, versendet das nächste Lager in der Reihenfolge oben, das alles hat – immer komplett, keine Teillieferungen.")}</span>
          </span>
        </label>

        <div>
          <div className="text-[12px] font-medium text-fg-muted flex items-center gap-1.5"><Globe2 className="size-3.5" /> {t("Länderregeln (vor dem Hauptlager)")}</div>
          <div className="mt-2 space-y-2">
            {rules.map((r, k) => (
              <div key={k} className="flex flex-wrap items-center gap-2">
                <Input
                  defaultValue={r.countries.join(", ")}
                  disabled={!editable}
                  onBlur={(e) => setRules(rules.map((x, j) => (j === k ? { ...x, countries: e.target.value.split(/[\s,;]+/).map((c) => c.trim().toUpperCase()).filter((c) => c.length === 2) } : x)))}
                  placeholder={t("CH, LI")}
                  className="w-40 font-mono"
                />
                <span className="text-[12.5px] text-fg-muted">→</span>
                <select disabled={!editable} value={r.warehouseId} onChange={(e) => setRules(rules.map((x, j) => (j === k ? { ...x, warehouseId: e.target.value } : x)))} className="h-8 rounded-lg border border-border bg-surface px-2 text-[13px]">
                  {active.map((w) => <option key={w.warehouseId} value={w.warehouseId}>{meta(w.warehouseId).name}</option>)}
                </select>
                {editable && <button type="button" onClick={() => setRules(rules.filter((_, j) => j !== k))} className="size-7 grid place-items-center rounded-md text-fg-subtle hover:text-danger" aria-label={t("Regel entfernen")}><Trash2 className="size-3.5" /></button>}
              </div>
            ))}
            {editable && active.length > 1 && (
              <Button size="sm" variant="ghost" icon={<Plus className="size-3.5" />} onClick={() => setRules([...rules, { countries: [], warehouseId: active.find((w) => w.warehouseId !== s.primaryId)?.warehouseId ?? s.primaryId }])}>{t("Länderregel")}</Button>
            )}
            {!rules.length && !editable && <p className="text-[12.5px] text-fg-subtle">{t("Keine")}</p>}
          </div>
        </div>
      </div>
    </Card>
  );
}
